import React, { useRef } from "react";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import * as XLSX from "xlsx";
import {
  useAddOrganization,
  useOrganization,
} from "../../hooks/useOrganization";
import { toast, ToastContainer } from "react-toastify";
import cityService from "../../services/cityService";
import countryService from "../../services/countryService";

export default function ImportOrganizations() {
  const { mutate: addOrganization } = useAddOrganization();
  const { data: existingOrganizations } = useOrganization();

  const fileInputRef = useRef(null);

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDownloadTemplate = () => {
    // Create template headers
    const headers = [
      "Name",
      "Organization ID",
      "Email",
      "Phone Number",
      "Contact Name",
      "Registration Number",
      "APO Number",
      "Country",
      "City",
    ];

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers]);

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Organizations Template");

    // Save file
    XLSX.writeFile(wb, "create_organizations_template.xlsx");
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          // Track processed APO numbers
          const processedAPONumbers = new Set();
          let successCount = 0;
          let skippedCount = 0;

          // Process and upload each organization
          for (const row of jsonData) {
            // Validate required fields
            if (
              !row["Name"] ||
              !row["Organization ID"] ||
              !row["Email"] ||
              !row["Country"] ||
              !row["City"]
            ) {
              toast.error(
                `Missing required fields in row: ${JSON.stringify(row)}`
              );
              continue;
            }

            try {
              // Check if APO number already exists in existing organizations
              const apoNumber = row["APO Number"] || "N/A";
              const existingOrg = existingOrganizations?.find(
                (org) => org.APO_number === apoNumber
              );

              // Check if APO number was already processed in this import
              if (processedAPONumbers.has(apoNumber)) {
                toast.warning(
                  `Skipping duplicate APO Number (${apoNumber}) in import file`
                );
                skippedCount++;
                continue;
              }

              if (existingOrg) {
                toast.warning(
                  `Skipping organization with existing APO Number: ${apoNumber}`
                );
                skippedCount++;
                continue;
              }

              // Convert city and country names to lowercase for comparison
              const cityName = row["City"].toString().toLowerCase();
              const countryName = row["Country"].toString().toLowerCase();

              const cityId = await cityService.getIdByName(cityName);
              const countryId = await countryService.getIdByName(countryName);

              if (!cityId || !countryId) {
                toast.error(
                  `Invalid City (${cityName}) or Country (${countryName}) in row: ${JSON.stringify(
                    row
                  )}`
                );
                continue;
              }

              const organizationData = {
                name: row["Name"] || "",
                organization_id: row["Organization ID"] || "",
                email: row["Email"] || "",
                phone_number: row["Phone Number"] || "N/A",
                contact_name: row["Contact Name"] || "N/A",
                registration_number: row["Registration Number"] || "N/A",
                APO_number: apoNumber,
                city_id: cityId,
                country_id: countryId,
              };

              addOrganization({
                ...organizationData,
                created_at: new Date().toISOString(),
              });
              processedAPONumbers.add(apoNumber);
              successCount++;
            } catch (error) {
              console.error("Error processing row:", row, error);
              toast.error(`Error processing row: ${JSON.stringify(row)}`);
            }
          }

          // Show summary toast
          toast.success(
            `Import completed: ${successCount} organizations added, ${skippedCount} skipped`
          );
          event.target.value = "";
        };
        reader.readAsArrayBuffer(file);
      } catch (error) {
        console.error("Error importing organizations:", error);
        toast.error(error.message || "Error importing organizations!");
      }
    }
  };

  return (
    <>
      <ToastContainer />
      <button
        onClick={handleDownloadTemplate}
        className="bg-yellowColor transition-colors duration-200 px-4 py-2  rounded-lg text-white font-medium flex gap-2 items-center"
      >
        <i className="fa-solid fa-file-arrow-down text-lg"></i>
        Download Template
      </button>

      <div>
        <input
          type="file"
          id="import-file"
          accept=".xlsx"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <label htmlFor="import-file">
          <button
            data-track="import-organizations-button"
            data-module="Organizations"
            type="button"
            onClick={handleClick}
            className="bg-green-600 hover:bg-green-700 transition-colors duration-200 px-4 py-2  rounded-lg text-white font-medium flex gap-2 items-center"
          >
            <FileUploadIcon />
            Import Organizations
          </button>
        </label>
      </div>
    </>
  );
}
